import { Link } from 'react-router-dom';
import { Check, ArrowRight, MapPin } from 'lucide-react';
import { ScrollReveal } from '@/hooks/useScrollReveal';
import { usePricing } from '@/context/PricingContext';

const RESULTS = [
  'Get more enquiries from local customers',
  'Turn website visitors into paying clients',
  'Rank higher on Google for local searches',
  'Load fast on every device — mobile first',
  'Stand out from competitors in your area',
  'Own a website that works as hard as you do',
];

const AREAS = ['Bournemouth', 'Poole', 'Christchurch', 'Winton', 'Boscombe', 'Westbourne', 'Southbourne', 'Charminster'];

export default function ResultsSection() {
  const pricing = usePricing();
  const llPrice = pricing.local_launch?.price || '399';
  return (
    <section id="results" data-testid="results-section" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left — Results */}
          <div>
            <ScrollReveal>
              <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-slate-400 block mb-4">
                Results That Matter
              </span>
              <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0A0A0A] dark:text-white mb-6">
                A Website That Actually <span className="text-[#00E5FF]">Works</span>
              </h2>
              <p className="text-base leading-relaxed text-slate-600 dark:text-slate-400 mb-8">
                Most websites just sit there looking pretty. Every site I build is designed with one goal in mind: getting your business more customers. Whether that means more phone calls, more form submissions, or more walk-ins, your website should be your hardest-working employee.
              </p>
            </ScrollReveal>
            <ScrollReveal delay={100}>
              <ul className="space-y-4 mb-8">
                {RESULTS.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 mt-0.5 flex-shrink-0 text-[#00E5FF]" strokeWidth={3} />
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{item}</span>
                  </li>
                ))}
              </ul>
            </ScrollReveal>
            <ScrollReveal delay={200}>
              <Link
                to="/services"
                data-testid="results-cta"
                className="group inline-flex items-center gap-2 text-sm font-semibold text-[#00B4D8] hover:text-[#00E5FF] transition-colors"
              >
                See how it works <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </ScrollReveal>
          </div>

          {/* Right — Local Serving Block */}
          <div>
            <ScrollReveal delay={150}>
              <div className="bg-[#F8F9FA] dark:bg-[#111111] border border-slate-200 dark:border-white/10 rounded-2xl p-8 md:p-10 mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-4 h-4 text-[#00E5FF]" />
                  <span className="font-['Outfit'] text-sm font-bold text-[#0A0A0A] dark:text-white">Serving Bournemouth & Dorset</span>
                </div>
                <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-400 mb-6">
                  I work with small businesses right here in the BCP area. From independent shops on the high street to tradespeople, gyms, barbers, restaurants, and professional services — I understand the local market because I am part of it. Every website I build is tailored to attract customers in your area and outperform your local competition.
                </p>
                <div className="flex flex-wrap gap-2">
                  {AREAS.map((area) => (
                    <span
                      key={area}
                      className="px-3 py-1 rounded-full text-xs font-medium border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 text-slate-500 dark:text-slate-400"
                    >
                      {area}
                    </span>
                  ))}
                </div>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={250}>
              <div className="bg-[#0A0A0A] dark:bg-[#111111] dark:border dark:border-white/10 rounded-2xl p-8 md:p-10 text-center">
                <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Websites from</p>
                <p className="font-['Outfit'] text-5xl font-black text-white mb-2">&pound;{llPrice}</p>
                <p className="text-sm text-slate-400 mb-6">Get online fast with a Local Launch site</p>
                <Link
                  to="/pricing"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#00E5FF] text-[#0A0A0A] px-6 py-3.5 rounded-full text-sm font-semibold hover:bg-white transition-all"
                >
                  View Pricing <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </div>
    </section>
  );
}
